import { Formik } from 'formik';
import React from 'react';
import { Buttons, CancelButton, ModalCard, Button, Form, FormMessage, Field, HelpTooltip } from '~/components/index.js';
import * as Yup from 'yup';
import { useForm } from '~/hooks';
import { useApi, useToast, useWorkspace } from '~/contexts';

function ShiftAllocationsConfirmation({ project, onSubmit, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const [{ isSubmitting, status, message }, form] = useForm();

  const toDaysOffset = ({ direction, offset, unit }) => {
    const scale = unit === 'week' ? 7 : 1;
    const sign = direction === 'backward' ? -1 : 1;
    return sign * offset * scale;
  };

  const handleSubmit = async (values) => {
    form.submit(values);

    const daysOffset = toDaysOffset(values);
    try {
      await api.www.workspaces(workspace.id).allocations().projectSchedule.shiftAllocations(project.id, {
        daysOffset,
        includePastAllocations: values.includePastAllocations,
      });

      onClose();
      onSubmit();
      toast.success('Project allocations have been shifted.');
      form.done();
    } catch ({ message }) {
      form.error({ message });
    }
  };

  return (
    <ModalCard title="Shift All Allocations" onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={{ direction: 'forward', offset: 1, unit: 'week', includePastAllocations: false }}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          direction: Yup.string().oneOf(['forward', 'backward']).required(),
          offset: Yup.number().label('Offset').min(1).max(100).required(),
          unit: Yup.string().oneOf(['day', 'week']).required(),
        })}>
        <Form>
          <ModalCard.Body>
            This will shift all allocations on the {project.name} project within {project.client.name}.
            <br />
            <br />
            Shift allocations{' '}
            <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
              <Field.SingleSelect name="direction" style={{ margin: '0 0.5rem', width: '8.5rem', textAlign: 'right' }}>
                <option value="forward">forward</option>
                <option value="backward">backward</option>
              </Field.SingleSelect>
            </span>{' '}
            by
            <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
              <Field.SingleSelect name="offset" style={{ margin: '0 0.5rem', width: '6rem', textAlign: 'right' }}>
                {[...Array(100).keys()].map((_, idx) => (
                  <option key={idx} value={idx + 1}>
                    {idx + 1}
                  </option>
                ))}
              </Field.SingleSelect>
            </span>{' '}
            <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
              <Field.SingleSelect name="unit" style={{ margin: '0 0.5rem', width: '8rem', textAlign: 'right' }}>
                <option value="day">day(s)</option>
                <option value="week">week(s)</option>
              </Field.SingleSelect>
            </span>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
              <Field.Checkbox name="includePastAllocations" label="Shift past allocations" />
              <HelpTooltip
                style={{ marginLeft: '.5rem' }}
                message="If unchecked, only allocations ending today or later will be shifted."
              />
            </div>
            {status && <FormMessage.Error spaceTop>{message}</FormMessage.Error>}
          </ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={onClose}>Close</CancelButton>
              <Button type="submit" isLoading={isSubmitting}>
                Shift Allocations
              </Button>
            </Buttons>
          </ModalCard.Footer>
        </Form>
      </Formik>
    </ModalCard>
  );
}

export default ShiftAllocationsConfirmation;
